import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, isOnline } from '../lib/supabase';
import { offlineStore } from '../lib/offlineStore';
import { enqueue } from '../lib/syncQueue';

// ─── Create Recipe ──────────────────────────────────────────────────────────

export function useCreateRecipe(householdId, memberId) {
  const [loading, setLoading] = useState(false);
  const createRecipe = async (recipe) => {
    if (!isOnline()) return { error: { message: 'No connection' } };
    setLoading(true);
    const { data, error } = await supabase
      .from('recipes')
      .insert({ household_id: householdId, created_by: memberId, ...recipe })
      .select()
      .single();
    setLoading(false);
    return error ? { error } : { data };
  };
  return { createRecipe, loading };
}

// ─── Recipes ─────────────────────────────────────────────────────────────────

export function useRecipes(householdId) {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const fetch = useCallback(async () => {
    if (!isOnline()) {
      const cached = await offlineStore.recipes.getAll();
      if (cached.length) { setRecipes(cached); setStale(true); }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('recipes')
      .select('*')
      .or(`household_id.is.null,household_id.eq.${householdId}`)
      .order('name');
    if (error) {
      setError(error.message);
      const cached = await offlineStore.recipes.getAll();
      if (cached.length) { setRecipes(cached); setStale(true); }
    } else {
      setRecipes(data);
      setError(null);
      setStale(false);
      offlineStore.recipes.putAll(data);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { recipes, loading, error, stale, refetch: fetch };
}

// ─── Family Members ──────────────────────────────────────────────────────────

export function useFamilyMembers(householdId) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const fetch = useCallback(async () => {
    if (!isOnline()) {
      const cached = await offlineStore.familyMembers.getAll();
      if (cached.length) { setMembers(cached); setStale(true); }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('family_members')
      .select('*')
      .eq('household_id', householdId)
      .is('archived_at', null)
      .order('created_at');
    if (error) {
      setError(error.message);
      const cached = await offlineStore.familyMembers.getAll();
      if (cached.length) { setMembers(cached); setStale(true); }
    } else {
      setMembers(data);
      setError(null);
      setStale(false);
      offlineStore.familyMembers.putAll(data);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  const addMember = async ({ name, type, avatar, daily_cal_target, allergies }) => {
    if (!isOnline()) return null;
    const { data, error } = await supabase.rpc('add_family_member', {
      p_name: name,
      p_type: type || 'kid',
      p_avatar: avatar || '\u{1F9D1}',
      p_daily_cal_target: daily_cal_target || 2000,
      p_allergies: allergies || [],
    });
    if (error) return null;
    await fetch();
    return data;
  };

  const updateMember = async (memberId, updates) => {
    if (!isOnline()) return null;
    const { error } = await supabase.rpc('update_family_member', {
      p_member_id: memberId,
      p_name: updates.name ?? null,
      p_type: updates.type ?? null,
      p_avatar: updates.avatar ?? null,
      p_daily_cal_target: updates.daily_cal_target ?? null,
      p_allergies: updates.allergies ?? null,
      // undefined → leave unchanged; '' → clear the photo (RPC maps '' to NULL).
      p_avatar_url: updates.avatar_url ?? null,
    });
    if (error) return null;
    await fetch();
    return true;
  };

  const archiveMember = async (memberId) => {
    if (!isOnline()) return;
    const { error } = await supabase.rpc('archive_family_member', {
      p_member_id: memberId,
    });
    if (!error) setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const removeMember = archiveMember;

  return { members, loading, error, stale, addMember, updateMember, archiveMember, removeMember, refetch: fetch };
}

// ─── Preference Swipes ───────────────────────────────────────────────────────

export function useSwipes(householdId) {
  const [swipes, setSwipes] = useState({});  // { memberId: { recipeId: true/false } }
  const [rawSwipes, setRawSwipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const groupSwipes = (data) => {
    const grouped = {};
    data.forEach(s => {
      if (!grouped[s.member_id]) grouped[s.member_id] = {};
      grouped[s.member_id][s.recipe_id] = s.liked;
    });
    return grouped;
  };

  const fetch = useCallback(async () => {
    if (!isOnline()) {
      const cached = await offlineStore.swipes.getAll();
      if (cached.length) { setSwipes(groupSwipes(cached)); setRawSwipes(cached); setStale(true); }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('preference_swipes')
      .select('member_id, recipe_id, liked, swiped_at')
      .in('member_id', (
        await supabase
          .from('family_members')
          .select('id')
          .eq('household_id', householdId)
      ).data?.map(m => m.id) || []);

    if (error) {
      setError(error.message);
      const cached = await offlineStore.swipes.getAll();
      if (cached.length) { setSwipes(groupSwipes(cached)); setRawSwipes(cached); setStale(true); }
    } else if (data) {
      setSwipes(groupSwipes(data));
      setRawSwipes(data);
      setError(null);
      setStale(false);
      offlineStore.swipes.putAll(data.map(s => ({
        id: `${s.member_id}-${s.recipe_id}`,
        ...s,
      })));
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  const recordSwipe = async (memberId, recipeId, liked) => {
    // Optimistic local update
    setSwipes(prev => ({
      ...prev,
      [memberId]: { ...(prev[memberId] || {}), [recipeId]: liked },
    }));

    if (!isOnline()) {
      const offlineId = `offline-${memberId}-${recipeId}`;
      offlineStore.swipes.put({ id: offlineId, member_id: memberId, recipe_id: recipeId, liked });
      await enqueue('swipe', { member_id: memberId, recipe_id: recipeId, liked });
      return;
    }
    await supabase
      .from('preference_swipes')
      .upsert({ member_id: memberId, recipe_id: recipeId, liked }, { onConflict: 'member_id,recipe_id' });
  };

  return { swipes, rawSwipes, loading, error, stale, recordSwipe, refetch: fetch };
}

// ─── Meal Plans ──────────────────────────────────────────────────────────────

export function useMealPlans(householdId) {
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const fetch = useCallback(async () => {
    if (!isOnline()) {
      const cached = await offlineStore.mealPlans.getAll();
      if (cached.length) {
        const latest = cached.sort((a, b) => (b.week_of || '').localeCompare(a.week_of || ''))[0];
        setCurrentPlan(latest);
        setStale(true);
      }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('meal_plans')
      .select('*')
      .eq('household_id', householdId)
      .order('week_of', { ascending: false })
      .limit(1)
      .single();
    // PGRST116 = no rows found, which is not an error for us (just no plan yet)
    if (error && error.code !== 'PGRST116') {
      setError(error.message);
      const cached = await offlineStore.mealPlans.getAll();
      if (cached.length) {
        const latest = cached.sort((a, b) => (b.week_of || '').localeCompare(a.week_of || ''))[0];
        setCurrentPlan(latest);
        setStale(true);
      }
    } else {
      if (!error) {
        setCurrentPlan(data);
        offlineStore.mealPlans.put(data);
      }
      setError(null);
      setStale(false);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  const savePlan = async (weekOf, plan) => {
    const localPlan = { week_of: weekOf, plan, generated_at: new Date().toISOString() };
    if (!isOnline()) {
      setCurrentPlan(localPlan);
      offlineStore.mealPlans.put({ id: `offline-${householdId}-${weekOf}`, household_id: householdId, ...localPlan });
      return;
    }
    const { data, error } = await supabase
      .from('meal_plans')
      .upsert(
        { household_id: householdId, week_of: weekOf, plan },
        { onConflict: 'household_id,week_of' }
      )
      .select()
      .single();
    if (!error) {
      setCurrentPlan(data);
      offlineStore.mealPlans.put(data);
    }
    return data;
  };

  return { currentPlan, loading, error, stale, savePlan, refetch: fetch };
}

// ─── Meal Feedback ───────────────────────────────────────────────────────────

export function useAllFeedback(householdId) {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const fetch = useCallback(async () => {
    if (!isOnline()) {
      const cached = await offlineStore.feedback.getAll();
      if (cached.length) { setFeedback(cached); setStale(true); }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('meal_feedback')
      .select('recipe_id, member_id, stars, feedback_at')
      .in('member_id', (
        await supabase
          .from('family_members')
          .select('id')
          .eq('household_id', householdId)
      ).data?.map(m => m.id) || []);

    if (error) {
      setError(error.message);
      const cached = await offlineStore.feedback.getAll();
      if (cached.length) { setFeedback(cached); setStale(true); }
    } else {
      setFeedback(data || []);
      setError(null);
      setStale(false);
      if (data) offlineStore.feedback.putAll(data.map(f => ({ id: `${f.member_id}-${f.recipe_id}`, ...f })));
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { feedback, loading, error, stale, refetch: fetch };
}

export function useFeedback(mealPlanId) {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const fetch = useCallback(async () => {
    if (!isOnline() || !mealPlanId) {
      if (!isOnline() && mealPlanId) {
        const cached = await offlineStore.feedback.getAllByIndex('meal_plan_id', mealPlanId);
        if (cached.length) { setFeedback(cached); setStale(true); }
      }
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('meal_feedback')
      .select('*')
      .eq('meal_plan_id', mealPlanId);
    if (error) {
      setError(error.message);
      const cached = await offlineStore.feedback.getAllByIndex('meal_plan_id', mealPlanId);
      if (cached.length) { setFeedback(cached); setStale(true); }
    } else {
      setFeedback(data || []);
      setError(null);
      setStale(false);
      if (data) offlineStore.feedback.putAll(data);
    }
    setLoading(false);
  }, [mealPlanId]);

  useEffect(() => { fetch(); }, [fetch]);

  const submitFeedback = async ({ recipeId, memberId, day, meal, stars, comment }) => {
    const entry = {
      meal_plan_id: mealPlanId,
      recipe_id: recipeId,
      member_id: memberId,
      day,
      meal,
      stars,
      comment: comment || null,
    };

    const localId = crypto.randomUUID();

    // Optimistic update
    setFeedback(prev => {
      const filtered = prev.filter(
        f => !(f.recipe_id === recipeId && f.member_id === memberId && f.day === day && f.meal === meal)
      );
      return [...filtered, { ...entry, id: localId, feedback_at: new Date().toISOString() }];
    });

    if (!isOnline()) {
      offlineStore.feedback.put({ ...entry, id: localId, feedback_at: new Date().toISOString() });
      await enqueue('feedback', entry);
      return;
    }
    await supabase
      .from('meal_feedback')
      .upsert(entry, { onConflict: 'meal_plan_id,recipe_id,member_id,day,meal' });
  };

  const getFeedbackFor = (recipeId, memberId, day, meal) => {
    return feedback.find(
      f => f.recipe_id === recipeId && f.member_id === memberId && f.day === day && f.meal === meal
    );
  };

  return { feedback, loading, error, stale, submitFeedback, getFeedbackFor, refetch: fetch };
}

// ─── Household Favorites ("cook again" list) ────────────────────────────────

export function useFavorites(householdId) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!isOnline() || !householdId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('household_favorites')
      .select('recipe_id, marked_by, marked_at')
      .eq('household_id', householdId);
    if (error) {
      setError(error.message);
    } else {
      setFavorites(data || []);
      setError(null);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  const favoriteIds = useMemo(() => new Set(favorites.map(f => f.recipe_id)), [favorites]);
  const isFavorite = useCallback((recipeId) => favoriteIds.has(recipeId), [favoriteIds]);

  // Household-level toggle: any member can add or remove a shared favorite.
  const toggleFavorite = async (recipeId, memberId) => {
    if (!householdId || !recipeId) return;
    const currentlyFav = favoriteIds.has(recipeId);

    // Optimistic update
    setFavorites(prev => currentlyFav
      ? prev.filter(f => f.recipe_id !== recipeId)
      : [...prev, { recipe_id: recipeId, marked_by: memberId || null, marked_at: new Date().toISOString() }]);

    if (!isOnline()) return; // online-only for now; favorites are non-critical

    if (currentlyFav) {
      const { error } = await supabase
        .from('household_favorites')
        .delete()
        .eq('household_id', householdId)
        .eq('recipe_id', recipeId);
      if (error) { setError(error.message); fetch(); }
    } else {
      const { error } = await supabase
        .from('household_favorites')
        .upsert(
          { household_id: householdId, recipe_id: recipeId, marked_by: memberId || null },
          { onConflict: 'household_id,recipe_id' }
        );
      if (error) { setError(error.message); fetch(); }
    }
  };

  return { favorites, favoriteIds, isFavorite, toggleFavorite, loading, error, refetch: fetch };
}

// ─── Meal Predictions (rating prediction game) ──────────────────────────────

export function usePredictions(mealPlanId) {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!isOnline() || !mealPlanId) { setLoading(false); return; }
    const { data, error } = await supabase
      .from('meal_predictions')
      .select('*')
      .eq('meal_plan_id', mealPlanId);
    if (error) {
      setError(error.message);
    } else {
      setPredictions(data || []);
      setError(null);
    }
    setLoading(false);
  }, [mealPlanId]);

  useEffect(() => { fetch(); }, [fetch]);

  const savePrediction = async ({ recipeId, memberId, day, meal, predictedStars }) => {
    if (!mealPlanId) return;
    const entry = {
      meal_plan_id: mealPlanId,
      recipe_id: recipeId,
      member_id: memberId,
      day,
      meal,
      predicted_stars: predictedStars,
    };

    // Optimistic update
    setPredictions(prev => {
      const filtered = prev.filter(
        p => !(p.recipe_id === recipeId && p.member_id === memberId && p.day === day && p.meal === meal)
      );
      return [...filtered, { ...entry, id: crypto.randomUUID(), predicted_at: new Date().toISOString() }];
    });

    if (!isOnline()) return; // online-only for now; predictions are non-critical
    const { error } = await supabase
      .from('meal_predictions')
      .upsert(entry, { onConflict: 'member_id,recipe_id,meal_plan_id,day,meal' });
    if (error) setError(error.message);
  };

  const getPredictionFor = (recipeId, memberId, day, meal) => {
    return predictions.find(
      p => p.recipe_id === recipeId && p.member_id === memberId && p.day === day && p.meal === meal
    );
  };

  return { predictions, loading, error, savePrediction, getPredictionFor, refetch: fetch };
}

// ─── Household Settings ─────────────────────────────────────────────────────

const DEFAULT_MEAL_TIMES = { breakfast: '07:30', lunch: '12:00', dinner: '18:30' };
// Which meals a household plans by default (dinner-first; see migration 023).
const DEFAULT_PLANNED_MEALS = ['dinner'];

export function useHouseholdSettings(householdId) {
  const [mealTimes, setMealTimes] = useState(DEFAULT_MEAL_TIMES);
  const [plannedMeals, setPlannedMeals] = useState(DEFAULT_PLANNED_MEALS);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    if (!householdId) { setLoading(false); return; }
    if (!isOnline()) { setLoading(false); return; }
    let { data, error } = await supabase
      .from('households')
      .select('meal_times, planned_meals')
      .eq('id', householdId)
      .single();
    if (error) {
      // planned_meals may not exist yet (pre-migration 023) — fall back so meal
      // times still load and deploy order can't break settings.
      ({ data, error } = await supabase
        .from('households')
        .select('meal_times')
        .eq('id', householdId)
        .single());
    }
    if (!error && data) {
      if (data.meal_times) setMealTimes({ ...DEFAULT_MEAL_TIMES, ...data.meal_times });
      if (data.planned_meals?.length) setPlannedMeals(data.planned_meals);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  const updateMealTimes = async (newTimes) => {
    if (!isOnline()) return;
    setMealTimes(newTimes);
    const { error } = await supabase.rpc('update_meal_times', {
      p_meal_times: newTimes,
    });
    if (error) console.error('Failed to update meal times:', error.message);
  };

  const updatePlannedMeals = async (meals) => {
    if (!isOnline() || !meals?.length) return; // never persist an empty selection
    setPlannedMeals(meals);
    const { error } = await supabase.rpc('update_planned_meals', {
      p_planned_meals: meals,
    });
    if (error) console.error('Failed to update planned meals:', error.message);
  };

  return { mealTimes, plannedMeals, loading, updateMealTimes, updatePlannedMeals };
}

// ─── Household Subscription ──────────────────────────────────────────────────

export function useSubscription(householdId) {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!householdId || !isOnline()) {
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from('household_subscriptions')
      .select('*')
      .eq('household_id', householdId)
      .maybeSingle();
    if (error) {
      setError(error.message);
    } else {
      setSubscription(data);
      setError(null);
    }
    setLoading(false);
  }, [householdId]);

  useEffect(() => { fetch(); }, [fetch]);

  return { subscription, loading, error, refetch: fetch };
}
